import React, { useEffect, useMemo, useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Icon,
  IconButton,
  Select,
  styles as ui,
  Textarea,
  TextField,
  useConfirm,
  useToast,
} from "stories/Admin";
import styles from "./JanazahPage.module.scss";
import { FuneralAgency, NoticeFields, noticeChanges } from "./janazah";

type CeremonyType = "JANAZAH" | "BURIAL" | "DUA" | "OTHER";

interface Ceremony {
  type: CeremonyType;
  label: string;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  place: string | null;
  address: string | null;
  note?: string | null;
  subtitle: string | null;
  info: string | null;
}

interface Relative {
  name: string;
  relation: string | null;
  phone: string | null;
}

/** En lagret annonse (DeathNoticeDto): mulige duplikater, og annonsen som oppdateres. */
export interface DuplicateNotice extends NoticeFields {
  id: number;
  sourceName: string;
  sourceUrl: string;
  janazahDate: string | null;
  manual?: boolean;
}

interface Draft {
  name: string;
  source: string;
  /** Byråets navn slik det står på annonsen. */
  agencyName?: string | null;
  sourceUrl: string | null;
  imageUrl: string | null;
  noticeImageUrl: string | null;
  birthDate: string | null;
  deathDate: string | null;
  city: string | null;
  ceremonies: Ceremony[];
  obituary: string | null;
  funeralInfo: string | null;
  relatives: Relative[];
  possibleDuplicates: DuplicateNotice[];
  warnings: string[];
  /** Satt når utkastet oppdaterer en annonse vi har. */
  targetId?: number | null;
}

export interface PushPreview {
  eligible: boolean;
  alreadySent: boolean;
  title: string;
  body: string;
  reason: string | null;
}

interface SaveResult {
  notice: DuplicateNotice;
  pushSent: boolean;
  push?: PushPreview | null;
}

/** Spør superadmin før janazah-pushen sendes, og sender den ved ja. Viser grunnen når den ikke kan sendes. */
export const usePushConfirm = () => {
  const { execute, lastError } = useFetch();
  const confirm = useConfirm();
  const toast = useToast();
  return async (id: number, name: string, preview?: PushPreview | null) => {
    const p =
      preview ??
      ((await execute(
        `/api/v1/death-notices/admin/${id}/push`,
        "GET",
      )) as PushPreview | null);
    if (!p) {
      toast.error("Kunne ikke hente push-forhåndsvisningen.");
      return false;
    }
    if (!p.eligible) {
      toast.info(`Ingen push for ${name}: ${p.reason ?? "kan ikke sendes"}.`);
      return false;
    }
    const ok = await confirm({
      title: p.alreadySent ? "Sende oppdatert push?" : "Sende push om janazah?",
      message: (
        <div>
          <p>
            {p.alreadySent
              ? "Det er sendt push for denne før. Alle som har skrudd på janazah-varsler får denne oppdateringen:"
              : "Alle som har skrudd på janazah-varsler får dette:"}
          </p>
          <div className={styles.pushPreview}>
            <b>{p.title}</b>
            <div>{p.body}</div>
          </div>
        </div>
      ),
      confirmLabel: "Send push",
      cancelLabel: "Ikke send",
    });
    if (!ok) return false;
    const sent = await execute(
      `/api/v1/death-notices/admin/${id}/push`,
      "POST",
    );
    if (!sent) {
      toast.error(
        `Pushen ble ikke sendt${lastError()?.message ? `: ${lastError()?.message}` : "."}`,
      );
      return false;
    }
    toast.success("Push er sendt.");
    return true;
  };
};

const ceremonyLabels: Record<CeremonyType, string> = {
  JANAZAH: "Janazah",
  BURIAL: "Gravlegging",
  DUA: "Fatiha / Dua",
  OTHER: "Annet",
};

const emptyCeremony = (type: CeremonyType): Ceremony => ({
  type,
  label: ceremonyLabels[type],
  date: null,
  startTime: null,
  endTime: null,
  place: null,
  address: null,
  subtitle: null,
  info: null,
});

/** «12:00:00» fra backend → «12:00» i tidsfeltet. */
const hhmm = (v: string | null) => (v ? v.slice(0, 5) : "");
const orNull = (v: string) => (v.trim() ? v : null);

const formatDate = (value: string | null): string => {
  if (!value) return "—";
  const [y, m, d] = value.split("-");
  return d && m && y ? `${d}.${m}.${y}` : value;
};

interface Props {
  onClose: () => void;
  onSaved: () => void;
  /** Annonsen bildet skal oppdatere (bare den). Uten: ny annonse. */
  target?: DuplicateNotice | null;
}

/**
 * Last opp et bilde av en dødsannonse: AI-en leser feltene, superadmin ser over og lagrer.
 * Med [Props.target] (eller «Oppdater denne» ved duplikat) oppdateres bare den annonsen, og superadmin ser hva
 * som endres før lagring. Etter lagring spørres superadmin om push (ny, eller «oppdatert»).
 */
const JanazahUpload: React.FC<Props> = ({
  onClose,
  onSaved,
  target: initialTarget = null,
}) => {
  const { execute, lastError } = useFetch();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [usePhoto, setUsePhoto] = useState(true);
  const [target, setTarget] = useState<DuplicateNotice | null>(initialTarget);
  const [error, setError] = useState<string | null>(null);
  const confirmPush = usePushConfirm();
  const [agencies, setAgencies] = useState<FuneralAgency[]>([]);

  useEffect(() => {
    execute("/api/v1/funeral-agencies/admin", "GET").then((list) => {
      if (Array.isArray(list)) setAgencies(list as FuneralAgency[]);
    });
    // Bare én gang når skjemaet åpnes.
  }, []);

  const pickFile = (f: File | null) => {
    setFile(f);
    setDraft(null);
    setError(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(f ? URL.createObjectURL(f) : null);
  };

  const read = async () => {
    if (!file) return;
    setIsReading(true);
    setError(null);
    const form = new FormData();
    form.append("file", file);
    const result = (await execute(
      "/api/v1/death-notices/admin/extract",
      "POST",
      form,
    )) as Draft | null;
    setIsReading(false);
    if (!result) {
      // Vis grunnen fra serveren (f.eks. «AI mener bildet ikke er en dødsannonse»), ikke bare en generell feil.
      const reason = lastError()?.message;
      setError(
        reason
          ? `AI kunne ikke lese bildet: ${reason}`
          : "AI kunne ikke lese bildet. Sjekk at det er en dødsannonse (PNG eller JPG) og prøv igjen.",
      );
      return;
    }
    if (target) {
      await startUpdate(target, result);
      return;
    }
    setDraft(result);
    setUsePhoto(!!result.imageUrl);
  };

  /** Gjør det AI-en leste om til en oppdatering av [t]: annonsen slik den blir, til gjennomsyn før lagring. */
  const startUpdate = async (t: DuplicateNotice, read: Draft) => {
    setIsReading(true);
    const merged = (await execute(
      `/api/v1/death-notices/admin/${t.id}/update-draft`,
      "POST",
      {
        ...read,
        possibleDuplicates: undefined,
        warnings: undefined,
        ceremonies: read.ceremonies.map((c) => ({ ...c, note: null })),
        sendPush: false,
      },
    )) as Draft | null;
    setIsReading(false);
    if (!merged) {
      setError(
        `Kunne ikke lage oppdateringen${lastError()?.message ? `: ${lastError()?.message}` : "."}`,
      );
      return;
    }
    setTarget(t);
    setDraft({
      ...merged,
      possibleDuplicates: [],
      warnings: [...read.warnings, ...merged.warnings],
    });
    setUsePhoto(!!merged.imageUrl);
  };

  const changes = useMemo(
    () =>
      target && draft
        ? noticeChanges(target, {
            ...draft,
            imageUrl: usePhoto ? draft.imageUrl : null,
          })
        : [],
    [target, draft, usePhoto],
  );

  const update = (patch: Partial<Draft>) =>
    setDraft((d) => (d ? { ...d, ...patch } : d));

  const updateCeremony = (i: number, patch: Partial<Ceremony>) =>
    setDraft((d) =>
      d
        ? {
            ...d,
            ceremonies: d.ceremonies.map((c, j) =>
              j === i ? { ...c, ...patch } : c,
            ),
          }
        : d,
    );

  const updateRelative = (i: number, patch: Partial<Relative>) =>
    setDraft((d) =>
      d
        ? {
            ...d,
            relatives: d.relatives.map((r, j) =>
              j === i ? { ...r, ...patch } : r,
            ),
          }
        : d,
    );

  const save = async () => {
    if (!draft || !draft.name.trim()) return;
    setIsSaving(true);
    const result = (await execute(
      target
        ? `/api/v1/death-notices/admin/${target.id}`
        : "/api/v1/death-notices/admin/manual",
      target ? "PUT" : "POST",
      {
        name: draft.name,
        source: draft.source,
        agencyName: draft.agencyName ?? null,
        sourceUrl: draft.sourceUrl,
        imageUrl: usePhoto ? draft.imageUrl : null,
        noticeImageUrl: draft.noticeImageUrl,
        birthDate: draft.birthDate,
        deathDate: draft.deathDate,
        city: draft.city,
        // Merknaden lages på nytt fra undertittel og info.
        ceremonies: draft.ceremonies.map((c) => ({ ...c, note: null })),
        obituary: draft.obituary,
        funeralInfo: draft.funeralInfo,
        relatives: draft.relatives.filter((r) => r.name.trim()),
        // Pushen sendes først når superadmin har bekreftet den (under).
        sendPush: false,
      },
    )) as SaveResult | null;
    setIsSaving(false);
    if (!result) {
      toast.error(
        `Kunne ikke lagre dødsannonsen${lastError()?.message ? `: ${lastError()?.message}` : "."}`,
      );
      return;
    }
    toast.success(
      target
        ? `${result.notice.name} er oppdatert.`
        : `${result.notice.name} er lagt til.`,
    );
    // Spør om push før skjemaet lukkes; listen lastes inn på nytt etterpå.
    await confirmPush(result.notice.id, result.notice.name, result.push);
    onSaved();
  };

  const busy = isReading || isSaving;

  return (
    <Card
      title={
        target ? `Oppdater «${target.name}» fra bilde` : "Last opp dødsannonse"
      }
      subtitle={
        target
          ? "AI leser bildet, og bare denne annonsen oppdateres. Du ser hva som endres før du lagrer."
          : "AI leser bildet. Se over feltene før du lagrer. Når annonsen dukker opp hos byrået, slås de sammen automatisk."
      }
      actions={
        <IconButton
          label="Lukk"
          onClick={onClose}
          disabled={busy}
          icon={<Icon name="x" size={16} strokeWidth={2} />}
        />
      }
    >
      <div className={styles.uploadGrid}>
        <div className={styles.uploadPreview}>
          {preview ? (
            <img src={preview} alt="Dødsannonsen som lastes opp" />
          ) : (
            <button
              type="button"
              className={styles.dropzone}
              onClick={() => fileRef.current?.click()}
            >
              <Icon name="upload" size={22} strokeWidth={2} />
              Velg bilde av dødsannonsen
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            className={ui.srOnly}
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
          <div className={styles.uploadActions}>
            {preview && (
              <Button
                variant="secondary"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
              >
                Velg et annet bilde
              </Button>
            )}
            <Button
              onClick={read}
              disabled={!file || busy}
              loading={isReading}
              icon={<Icon name="wand" size={16} strokeWidth={2} />}
            >
              {isReading
                ? "AI leser bildet …"
                : draft
                  ? "Les på nytt"
                  : "Les med AI"}
            </Button>
          </div>
        </div>

        <div className={styles.uploadForm}>
          {error && <Alert tone="warn">{error}</Alert>}
          {!draft && !error && (
            <p className={ui.help}>
              Velg bildet og trykk «Les med AI». Feltene AI-en finner vises her
              før noe lagres.
            </p>
          )}

          {draft && (
            <>
              {!target && draft.possibleDuplicates.length > 0 && (
                <Alert tone="warn">
                  Denne personen ser ut til å finnes allerede. Oppdater den i
                  stedet, så ser du hva som endres før du lagrer:
                  <div className={styles.duplicateList}>
                    {draft.possibleDuplicates.map((d) => (
                      <div key={d.id} className={styles.duplicateRow}>
                        <span>
                          <b>{d.name}</b> ({d.sourceName}
                          {d.janazahDate
                            ? `, janazah ${formatDate(d.janazahDate)}`
                            : ""}
                          )
                        </span>
                        <Button
                          size="sm"
                          onClick={() => startUpdate(d, draft)}
                          disabled={busy}
                          icon={<Icon name="edit" size={14} strokeWidth={2} />}
                        >
                          Oppdater denne
                        </Button>
                      </div>
                    ))}
                  </div>
                  Lagrer du som ny, blir det en annonse til.
                </Alert>
              )}
              {target && (
                <div className={styles.changes}>
                  <h3 className={styles.detailTitle}>
                    Dette endres i «{target.name}»
                  </h3>
                  {changes.length === 0 ? (
                    <p className={ui.help}>Ingen endringer.</p>
                  ) : (
                    <ul className={styles.changeList}>
                      {changes.map((c, i) => (
                        <li key={i}>
                          <b>{c.label}</b>
                          {c.before && (
                            <div className={styles.changeBefore}>
                              {c.before}
                            </div>
                          )}
                          <div className={styles.changeAfter}>
                            {c.after ?? <i>Fjernes</i>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {draft.warnings.length > 0 && (
                <Alert tone="info">
                  <ul className={styles.warningList}>
                    {draft.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              <div className={styles.personRow}>
                {draft.imageUrl && (
                  <div className={styles.portrait}>
                    <img
                      src={draft.imageUrl}
                      alt="Portrett fra annonsen"
                      className={usePhoto ? "" : styles.portraitOff}
                    />
                    <Checkbox
                      label="Bruk bildet"
                      checked={usePhoto}
                      onChange={(e) => setUsePhoto(e.target.checked)}
                    />
                  </div>
                )}
                <div className={ui.g2}>
                  <TextField
                    label="Navn"
                    required
                    value={draft.name}
                    onChange={(e) => update({ name: e.target.value })}
                  />
                  <Select
                    label="Byrå"
                    value={draft.source}
                    onChange={(e) => update({ source: e.target.value })}
                  >
                    {(agencies.length > 0
                      ? agencies
                      : [
                          { key: "ALKHIDMAT", name: "Al-Khidmat" },
                          { key: "MUSLIMSK", name: "Muslimsk Begravelsesbyrå" },
                          { key: "ALJANNAH", name: "Al-Jannah" },
                        ]
                    ).map((a) => (
                      <option key={a.key} value={a.key}>
                        {a.name}
                      </option>
                    ))}
                    <option value="USER">
                      Annet byrå
                      {draft.agencyName ? ` (${draft.agencyName})` : ""}
                    </option>
                  </Select>
                  <TextField
                    label="Født"
                    type="date"
                    value={draft.birthDate ?? ""}
                    onChange={(e) =>
                      update({ birthDate: orNull(e.target.value) })
                    }
                  />
                  <TextField
                    label="Død"
                    type="date"
                    value={draft.deathDate ?? ""}
                    onChange={(e) =>
                      update({ deathDate: orNull(e.target.value) })
                    }
                  />
                  <TextField
                    label="By"
                    value={draft.city ?? ""}
                    onChange={(e) => update({ city: orNull(e.target.value) })}
                  />
                  <TextField
                    label="Lenke til minnesiden"
                    help="Valgfritt. Uten lenke vises bildet av annonsen som kilde."
                    value={draft.sourceUrl ?? ""}
                    onChange={(e) =>
                      update({ sourceUrl: orNull(e.target.value) })
                    }
                  />
                </div>
              </div>

              <h3 className={styles.detailTitle}>Seremonier</h3>
              {draft.ceremonies.map((c, i) => (
                <div key={i} className={styles.editBlock}>
                  <div className={styles.editBlockHead}>
                    <Badge
                      tone={
                        c.type === "JANAZAH"
                          ? "mint"
                          : c.type === "DUA"
                            ? "blue"
                            : c.type === "BURIAL"
                              ? "grey"
                              : "lilac"
                      }
                    >
                      {ceremonyLabels[c.type]}
                    </Badge>
                    <IconButton
                      label="Fjern seremonien"
                      onClick={() =>
                        update({
                          ceremonies: draft.ceremonies.filter(
                            (_, j) => j !== i,
                          ),
                        })
                      }
                      icon={<Icon name="trash" size={15} strokeWidth={2} />}
                    />
                  </div>
                  <div className={ui.g3}>
                    <Select
                      label="Type"
                      value={c.type}
                      onChange={(e) =>
                        updateCeremony(i, {
                          type: e.target.value as CeremonyType,
                        })
                      }
                    >
                      {(Object.keys(ceremonyLabels) as CeremonyType[]).map(
                        (t) => (
                          <option key={t} value={t}>
                            {ceremonyLabels[t]}
                          </option>
                        ),
                      )}
                    </Select>
                    <TextField
                      label="Overskrift"
                      value={c.label}
                      onChange={(e) =>
                        updateCeremony(i, { label: e.target.value })
                      }
                    />
                    <TextField
                      label="Dato"
                      type="date"
                      value={c.date ?? ""}
                      onChange={(e) =>
                        updateCeremony(i, { date: orNull(e.target.value) })
                      }
                    />
                    <TextField
                      label="Fra kl."
                      type="time"
                      value={hhmm(c.startTime)}
                      onChange={(e) =>
                        updateCeremony(i, {
                          startTime: orNull(e.target.value),
                        })
                      }
                    />
                    <TextField
                      label="Til kl."
                      type="time"
                      value={hhmm(c.endTime)}
                      onChange={(e) =>
                        updateCeremony(i, { endTime: orNull(e.target.value) })
                      }
                    />
                    <TextField
                      label="Sted"
                      value={c.place ?? ""}
                      onChange={(e) =>
                        updateCeremony(i, { place: orNull(e.target.value) })
                      }
                    />
                    <TextField
                      label="Adresse"
                      value={c.address ?? ""}
                      onChange={(e) =>
                        updateCeremony(i, { address: orNull(e.target.value) })
                      }
                    />
                    <TextField
                      label="Undertittel"
                      placeholder="F.eks. For både menn og kvinner"
                      value={c.subtitle ?? ""}
                      onChange={(e) =>
                        updateCeremony(i, { subtitle: orNull(e.target.value) })
                      }
                    />
                    <TextField
                      label="Info"
                      placeholder="F.eks. parkering"
                      value={c.info ?? ""}
                      onChange={(e) =>
                        updateCeremony(i, { info: orNull(e.target.value) })
                      }
                    />
                  </div>
                </div>
              ))}
              <div className={styles.uploadActions}>
                {(["JANAZAH", "BURIAL", "DUA"] as CeremonyType[]).map((t) => (
                  <Button
                    key={t}
                    variant="ghost"
                    size="sm"
                    icon={<Icon name="plus" size={14} strokeWidth={2} />}
                    onClick={() =>
                      update({
                        ceremonies: [...draft.ceremonies, emptyCeremony(t)],
                      })
                    }
                  >
                    {ceremonyLabels[t]}
                  </Button>
                ))}
              </div>

              <h3 className={styles.detailTitle}>Pårørende</h3>
              {draft.relatives.map((r, i) => (
                <div key={i} className={styles.relativeRow}>
                  <TextField
                    aria-label="Navn"
                    placeholder="Navn"
                    value={r.name}
                    onChange={(e) =>
                      updateRelative(i, { name: e.target.value })
                    }
                  />
                  <TextField
                    aria-label="Relasjon"
                    placeholder="Relasjon"
                    value={r.relation ?? ""}
                    onChange={(e) =>
                      updateRelative(i, { relation: orNull(e.target.value) })
                    }
                  />
                  <TextField
                    aria-label="Telefon"
                    placeholder="Telefon"
                    value={r.phone ?? ""}
                    onChange={(e) =>
                      updateRelative(i, { phone: orNull(e.target.value) })
                    }
                  />
                  <IconButton
                    label="Fjern"
                    onClick={() =>
                      update({
                        relatives: draft.relatives.filter((_, j) => j !== i),
                      })
                    }
                    icon={<Icon name="trash" size={15} strokeWidth={2} />}
                  />
                </div>
              ))}
              <div className={styles.uploadActions}>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Icon name="plus" size={14} strokeWidth={2} />}
                  onClick={() =>
                    update({
                      relatives: [
                        ...draft.relatives,
                        { name: "", relation: null, phone: null },
                      ],
                    })
                  }
                >
                  Pårørende
                </Button>
              </div>

              <div className={ui.g2}>
                <Textarea
                  label="Minnetekst"
                  rows={4}
                  value={draft.obituary ?? ""}
                  onChange={(e) => update({ obituary: orNull(e.target.value) })}
                />
                <Textarea
                  label="Informasjon om gravferden"
                  rows={4}
                  value={draft.funeralInfo ?? ""}
                  onChange={(e) =>
                    update({ funeralInfo: orNull(e.target.value) })
                  }
                />
              </div>

              <div className={styles.saveRow}>
                <span className={ui.help}>
                  Du får spørsmål om push etter at du har lagret.
                </span>
                <Button
                  onClick={save}
                  disabled={
                    busy ||
                    !draft.name.trim() ||
                    (!!target && changes.length === 0)
                  }
                  loading={isSaving}
                  icon={<Icon name="check" size={16} strokeWidth={2} />}
                >
                  {target
                    ? `Lagre ${changes.length} endring${changes.length === 1 ? "" : "er"}`
                    : "Lagre dødsannonse"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default JanazahUpload;
